import React from "react";
import { Box, Container, Paper, Tab, Tabs, Typography } from "@mui/material";
import { HiOfficeBuilding, HiUserGroup, HiUser } from "react-icons/hi";
import { useNavigate, useLocation } from "react-router-dom";
import AdminV2Breadcrumbs from "./Breadcrumbs";
import OrganizationsTable from "./organizations/OrganizationsTable";
import SystemPropertiesCard from "./organizations/SystemPropertiesCard";
import AssignmentsTable from "./assignments/AssignmentsTable";
import UsersTable from "./users/UsersTable";
import styles from "./AdminV2Dashboard.module.scss";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-v2-tabpanel-${index}`}
      aria-labelledby={`admin-v2-tab-${index}`}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `admin-v2-tab-${index}`,
    "aria-controls": `admin-v2-tabpanel-${index}`,
  };
}

const TAB_CONFIG = [
  {
    label: "Organizaciones y Roles",
    path: "/admin-v2",
    icon: <HiOfficeBuilding size={20} />,
  },
  {
    label: "Asignaciones de Usuarios",
    path: "/admin-v2/assignments",
    icon: <HiUserGroup size={20} />,
  },
  {
    label: "Usuarios",
    path: "/admin-v2/users",
    icon: <HiUser size={20} />,
  },
] as const;

const AdminV2Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const currentTab = location.pathname.startsWith("/admin-v2/assignments")
    ? 1
    : location.pathname.startsWith("/admin-v2/users")
      ? 2
      : 0;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    navigate(TAB_CONFIG[newValue].path);
  };

  return (
    <Container maxWidth="xl" className={styles.dashboard}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
          Panel de Administración
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gestiona organizaciones, roles y asignaciones de usuarios
        </Typography>
      </Box>

      <AdminV2Breadcrumbs
        items={[{ label: "Admin V2", path: "/admin-v2" }, { label: TAB_CONFIG[currentTab].label }]}
      />

      <Paper elevation={2} sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="Admin V2 navigation tabs"
            variant="fullWidth"
          >
            {TAB_CONFIG.map((tab, index) => (
              <Tab
                key={tab.path}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {tab.icon}
                    <span>{tab.label}</span>
                  </Box>
                }
                {...a11yProps(index)}
              />
            ))}
          </Tabs>
        </Box>

        <TabPanel value={currentTab} index={0}>
          <SystemPropertiesCard />
          <OrganizationsTable />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <AssignmentsTable />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <UsersTable />
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default AdminV2Dashboard;
